import React, { useCallback, useMemo } from "react";

import sortBy from "lodash/sortBy";

import { TaskActivityLog } from "@fartherfinance/frontend/api/Tasks/hooks/useGetTaskActivityLog";
import useRemoveAttachment from "@fartherfinance/frontend/api/Tasks/hooks/useRemoveAttachment";
import useRemoveTaskComment from "@fartherfinance/frontend/api/Tasks/hooks/useRemoveTaskComment";
import useUpdateTaskComment from "@fartherfinance/frontend/api/Tasks/hooks/useUpdateTaskComment";
import {
  TaskActivityWithModifierProfile,
  TaskCommentWithAuthorRoles,
  TaskDetailsV2,
  TaskDetailsV3,
  TaskManipulator,
} from "@fartherfinance/frontend/api/Tasks/Types";
import {
  AttachmentId,
  TaskCommentId,
} from "@fartherfinance/frontend/api/Types";

import TaskCommentComponent from "../TaskComment/TaskComment";
import TaskSystemComment from "../TaskComment/TaskSystemComment";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { useTaskActivityOrder } from "@src/multiCustodian/pages/Advisor/Tasks/hooks/useTaskActivityOrder";
import { formatTaskDate } from "@src/multiCustodian/pages/Advisor/Tasks/TasksTable/TasksTable.utils";
import { trackEvent } from "@src/multiCustodian/services/tracking";
import { toClassName } from "@src/multiCustodian/utils/to-class-name";
import Tooltip from "@src/sharedComponents/Tooltip/Tooltip";
import Text from "@src/sharedComponents/Typography/Text";
import { extractAxiosErrorMessage } from "@src/utils/axios";

import styles from "./TaskActivity.module.css";

const activityManipulatorToTaskManipulator = (
  modifier: NonNullable<
    TaskActivityWithModifierProfile["meta"]
  >["data"]["modifier"]
): TaskManipulator => {
  if (modifier.type === "HUMAN") {
    return {
      ...modifier,
      name: {
        first: modifier.profile?.name.first ?? "Unknown",
        middle: null,
        last: modifier.profile?.name.last ?? "Person",
      },
    };
  }

  return modifier;
};

const isAdvisorTaskActivity = (
  activity: NonNullable<TaskActivityWithModifierProfile["meta"]>
): boolean => {
  const { modifier } = activity.data;
  if (modifier.type !== "HUMAN" || !modifier.profile) {
    return false;
  }
  return modifier.profile.roles.includes("Farther Advisor");
};

const isAdvisorTaskComment = (comment: TaskCommentWithAuthorRoles): boolean =>
  comment.author.roles.includes("Farther Advisor");

interface TaskActivityProps {
  taskDetails: TaskDetailsV2 | TaskDetailsV3;
  isEmbedded?: boolean;
  taskActivities?: TaskActivityLog["data"];
}

const TaskActivityV2: React.FC<TaskActivityProps> = ({
  taskDetails,
  isEmbedded,
  taskActivities,
}) => {
  const auth = useAdvisorRequestAuth();
  const statusNotification = useStatusNotification();
  const callRemoveTaskComment = useRemoveTaskComment(auth);
  const callUpdateTaskComment = useUpdateTaskComment(auth);
  const callRemoveAttachment = useRemoveAttachment(auth);

  const handleRemoveTaskComment = async (
    commentId: TaskCommentId
  ): Promise<void> => {
    try {
      await callRemoveTaskComment({ taskId: taskDetails.id, commentId });

      trackEvent({ name: "Advisor Tasks Comment Delete" });
    } catch (error) {
      statusNotification(
        extractAxiosErrorMessage(error, "Failed to delete comment."),
        "Error"
      );
    }
  };

  const handleUpdateTaskComment = async (
    commentId: TaskCommentId,
    content: string
  ): Promise<void> => {
    try {
      await callUpdateTaskComment({
        taskId: taskDetails.id,
        commentId,
        request: { content },
      });

      trackEvent({ name: "Advisor Tasks Comment Update" });
    } catch (error) {
      statusNotification(
        extractAxiosErrorMessage(error, "Failed to update comment."),
        "Error"
      );
    }
  };

  const handleDeleteAttachment = async (
    attachmentId: AttachmentId
  ): Promise<void> => {
    try {
      await callRemoveAttachment({ taskId: taskDetails.id, attachmentId });

      trackEvent({ name: "Advisor Tasks Attachment Delete" });

      statusNotification("Attachment removed successfully.", "Success");
    } catch (error) {
      statusNotification(
        extractAxiosErrorMessage(error, "Failed to delete attachment."),
        "Error"
      );
    }
  };

  const { activityOrder, toggleOrder, orderTooltip } = useTaskActivityOrder();

  const handleToggleOrder = useCallback(() => {
    toggleOrder();
    // Use type assertion until TrackingEvents type is updated
    trackEvent({ name: "Advisor Tasks Activity Order Toggle" } as any);
  }, [toggleOrder]);

  const sortedActivities = useMemo(() => {
    if (!taskActivities) {
      return [];
    }

    const activities = [...taskActivities];

    const sorted = sortBy(activities, (activity) =>
      "createdTs" in activity
        ? new Date(activity.createdTs).getTime()
        : activity.timestamp.getTime()
    );

    return activityOrder === "newest-first" ? sorted.toReversed() : sorted;
  }, [taskActivities, activityOrder]);

  return (
    <div
      className={toClassName(styles.container, {
        [styles.embedded]: isEmbedded,
      })}
    >
      <div className={styles.activityHeader}>
        <div className={styles.activityTitle}>
          <Text variant="title3">Activity</Text>
          <Tooltip tooltipText={orderTooltip}>
            <span
              className={styles.sortIcon}
              onClick={handleToggleOrder}
              data-testid="task-activity-sort-toggle"
            >
              {activityOrder === "oldest-first" ? "↑" : "↓"}
            </span>
          </Tooltip>
        </div>
        <div>
          {taskDetails.modifiedBy && (
            <Text variant="body">
              Updated: {formatTaskDate(new Date(taskDetails.updatedTs))}
            </Text>
          )}
        </div>
      </div>

      <div className={styles.commentsContainer}>
        {sortedActivities.map((item) => {
          if ("createdTs" in item) {
            return (
              <TaskCommentComponent
                key={item.id}
                comment={item}
                isAdvisor={isAdvisorTaskComment(item)}
                onCommentDelete={handleRemoveTaskComment}
                onCommentUpdate={handleUpdateTaskComment}
                onAttachmentDelete={handleDeleteAttachment}
              />
            );
          } else {
            return (
              item.meta && (
                <TaskSystemComment
                  key={item.activityId}
                  manipulator={activityManipulatorToTaskManipulator(
                    item.meta.data.modifier
                  )}
                  isAdvisor={isAdvisorTaskActivity(item.meta)}
                  comment={item.body}
                  timestamp={item.timestamp.toISOString()}
                />
              )
            );
          }
        })}
      </div>
    </div>
  );
};

export default TaskActivityV2;
